import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getInstances, disableInstance, enableInstance, setInstanceEnvironment,
  approveInstance, resetInstanceToken, getSecurityPolicy,
} from '../../services/api';
import { pill } from '../pill';
import Button from '../ui/Button';
import Modal from '../ui/Modal';

const ENVIRONMENTS = ['INTEGRATION', 'VALIDATION', 'PREPROD', 'PROD', 'TEST'];

/**
 * Instances serveur. DO-207 : une nouvelle instance est « à approuver » et ne reçoit
 * aucun package avant approbation ; colonne Authentification = jeton propre à
 * l'instance ou clé de licence seule (back Digibrain pas encore à jour, accepté tant
 * que le mode transitoire INSTANCE_LEGACY_AUTH est actif).
 */
export default function InstancesPage() {
  const [instances, setInstances] = useState([]);
  const [filter, setFilter] = useState('all'); // all | pending | active | disabled
  const [disableModal, setDisableModal] = useState(null);
  const [reason, setReason] = useState('');
  const [tokenModal, setTokenModal] = useState(null);   // instance dont on réinitialise le jeton
  const [policy, setPolicy] = useState(null);
  const navigate = useNavigate();

  const load = () => getInstances().then(setInstances);
  useEffect(() => {
    load();
    getSecurityPolicy().then(setPolicy).catch(() => {});
  }, []);

  const pending = instances.filter(i => i.approvalStatus === 'PENDING');
  const legacy = instances.filter(i => i.approvalStatus === 'APPROVED' && i.lastAuthMode === 'LEGACY');

  const filtered = instances.filter(i => {
    if (filter === 'pending') return i.approvalStatus === 'PENDING';
    if (filter === 'active') return i.active;
    if (filter === 'disabled') return !i.active;
    return true;
  });

  const handleDisable = async () => {
    await disableInstance(disableModal, reason);
    setDisableModal(null);
    setReason('');
    load();
  };

  const handleEnable = async id => { await enableInstance(id); load(); };

  const handleApprove = async id => { await approveInstance(id); load(); };

  const handleResetToken = async () => {
    await resetInstanceToken(tokenModal.id);
    setTokenModal(null);
    load();
  };

  const handleEnvironment = async (id, environment) => {
    await setInstanceEnvironment(id, environment);
    load();
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ margin: 0 }}>Instances serveur</h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {['all', 'pending', 'active', 'disabled'].map(f => (
            <Button key={f} size="sm" variant={filter === f ? 'primary' : 'secondary'} onClick={() => setFilter(f)}>
              {f === 'all' ? 'Toutes' : f === 'pending' ? `À approuver (${pending.length})`
                : f === 'active' ? 'Actives' : 'Désactivées'}
            </Button>
          ))}
        </div>
      </div>

      {pending.length > 0 && (
        <div style={banner('#fff4e0', '#e0b25e', '#955d00')}>
          {pending.length} instance(s) à approuver : elles ne reçoivent aucun package tant qu'elles
          ne sont pas approuvées.
        </div>
      )}
      {policy?.instanceLegacyAuth && legacy.length > 0 && (
        <div style={banner('#eef0f7', '#c9cfe6', '#232853')}>
          Mode transitoire actif : {legacy.length} instance(s) s'authentifient encore par la seule clé
          de licence (back Digibrain sans jeton d'instance). Passer INSTANCE_LEGACY_AUTH=false une fois
          toutes les instances mises à jour.
        </div>
      )}

      <div style={{ background: '#ffffff', borderRadius: '12px', overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: '1000px', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ color: '#8a8f99', fontSize: '0.8rem', textTransform: 'uppercase', borderBottom: '1px solid #cdcdcd' }}>
              {['Client', 'Serveur', 'IP', 'Environnement', 'Derniers users', 'Dernière activité', 'Statut', 'Authentification', 'Actions'].map(h =>
                <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {filtered.map(inst => (
              <tr key={inst.id} style={{ borderBottom: '1px solid #ececec' }}
                  className="ui-row">
                <td style={td}>
                  <button onClick={() => navigate(`/licenses/${inst.licenseId}`)} style={linkText}>
                    {inst.clientName}
                  </button>
                </td>
                <td style={td}>{inst.serverName}</td>
                <td style={{ ...td, fontFamily: 'monospace', color: '#6b7280', fontSize: '0.85rem' }}>{inst.serverIp}</td>
                <td style={td}>
                  <select value={inst.environment || ''}
                          onChange={e => handleEnvironment(inst.id, e.target.value)}
                          style={{ background: '#f5f5f5', border: '1px solid #cdcdcd', color: inst.environment ? '#555555' : '#8a8f99', borderRadius: '6px', padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}>
                    <option value="">non déclaré</option>
                    {ENVIRONMENTS.map(env => <option key={env} value={env}>{env}</option>)}
                  </select>
                </td>
                <td style={{ ...td, fontWeight: 600 }}>{inst.lastReportedUsers ?? '—'}</td>
                <td style={{ ...td, color: '#6b7280', fontSize: '0.82rem' }}>
                  {inst.lastSeenAt ? new Date(inst.lastSeenAt).toLocaleString('fr-FR') : 'Jamais'}
                </td>
                <td style={td}>
                  {!inst.active
                    ? <span title={inst.disableReason} style={badge('#fdecea', '#c62828')}>Désactivée ⓘ</span>
                    : inst.approvalStatus === 'PENDING'
                      ? <>
                          <span style={badge('#fff4e0', '#955d00')}>À approuver</span>
                          <Button size="sm" variant="success" onClick={() => handleApprove(inst.id)}
                                  style={{ marginTop: '0.35rem', width: '100%' }}>
                            Approuver
                          </Button>
                        </>
                      : <span style={badge('#e2f3e8', '#1a7f37')}
                              title={inst.approvedBy ? `Approuvée par ${inst.approvedBy} le ${formatDate(inst.approvedAt)}` : ''}>
                          Active
                        </span>}
                </td>
                <td style={td}>
                  <AuthBadge inst={inst} />
                  {inst.tokenStatus !== 'NONE' && (
                    <button type="button" style={linkSmall} onClick={() => setTokenModal(inst)}>
                      réinitialiser le jeton
                    </button>
                  )}
                </td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  {inst.active
                    ? <Button size="sm" variant="danger" onClick={() => setDisableModal(inst.id)}>Désactiver</Button>
                    : <Button size="sm" variant="success" onClick={() => handleEnable(inst.id)}>Réactiver</Button>}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={9} style={{ ...td, textAlign: 'center', color: '#8a8f99', padding: '3rem' }}>Aucune instance</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {tokenModal && (
        <Modal title={`Réinitialiser le jeton de ${tokenModal.serverName}`} width={500} onClose={() => setTokenModal(null)}
               footer={<>
                 <Button onClick={() => setTokenModal(null)}>Annuler</Button>
                 <Button variant="danger" onClick={handleResetToken}>Réinitialiser</Button>
               </>}>
          <p style={{ margin: 0, fontSize: '0.9rem', lineHeight: 1.5 }}>
            Le jeton actuel cesse de fonctionner. Un nouveau est remis au prochain check-in de
            l'instance avec la clé de licence et ce nom d'instance — à faire uniquement si l'instance
            a perdu son jeton (base restaurée, réinstallation) ou en cas de fuite.
          </p>
        </Modal>
      )}

      {disableModal && (
        <Modal title="Désactiver l'instance" width={460} onClose={() => setDisableModal(null)}
               footer={<>
                 <Button onClick={() => setDisableModal(null)}>Annuler</Button>
                 <Button variant="danger" onClick={handleDisable}>Confirmer la désactivation</Button>
               </>}>
            <textarea
              style={{ width: '100%', height: '80px', background: '#f5f5f5', border: '1px solid #cdcdcd', color: '#444444', padding: '0.6rem', borderRadius: '6px', resize: 'vertical', boxSizing: 'border-box' }}
              placeholder="Motif de désactivation (dépassement de licence, etc.)..."
              value={reason}
              onChange={e => setReason(e.target.value)}
            />
        </Modal>
      )}
    </div>
  );
}

/** Jeton propre à l'instance (DO-207), ou clé de licence seule (mode transitoire). */
function AuthBadge({ inst }) {
  if (inst.approvalStatus === 'PENDING') return <span style={{ color: '#b0b5bf' }}>—</span>;
  if (inst.lastAuthMode === 'TOKEN') {
    return <span style={badge('#e2f3e8', '#1a7f37')}
                 title={`Jeton utilisé depuis le ${formatDate(inst.tokenConfirmedAt)}`}>Jeton</span>;
  }
  if (inst.lastAuthMode === 'LEGACY') {
    return <span style={badge('#fff4e0', '#955d00')}
                 title="Back Digibrain sans jeton d'instance : accepté tant que INSTANCE_LEGACY_AUTH=true">
             Clé seule
           </span>;
  }
  return <span style={{ color: '#b0b5bf', fontSize: '0.82rem' }}>pas encore connectée</span>;
}

const formatDate = v => (v ? new Date(v).toLocaleString('fr-FR') : '—');
const banner = (bg, border, color) => ({
  background: bg, border: `1px solid ${border}`, color, borderRadius: '8px',
  padding: '0.65rem 0.9rem', marginBottom: '1rem', fontSize: '0.88rem',
});
const td = { padding: '0.85rem 1rem', color: '#444444' };
const linkSmall = { display: 'block', margin: '0.3rem auto 0', background: 'none', border: 'none', padding: 0, color: '#6376aa', cursor: 'pointer', fontSize: '0.75rem', textDecoration: 'underline' };
const badge = pill;
const linkText = { background: 'none', border: 'none', color: '#005cc5', cursor: 'pointer', padding: 0, fontSize: 'inherit' };
